"use client";

import { motion } from "framer-motion";
import {
  Globe,
  DollarSign,
  HeartHandshake,
  Building2,
  ShoppingCart,
  Megaphone,
  Store,
  UserCheck,
  Target,
  Rocket,
  Clock,
  BarChart3,
  ArrowRight,
  Gem,
} from "lucide-react";
import { Container } from "@/components/ui/container";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { StarRating } from "@/components/ui/star-rating";
import { productStats } from "@/lib/constants";
import {
  BrandPartnershipsIllustration,
  AiAssistantIllustration,
} from "@/components/illustrations";

const showcaseData = {
  brandPartnerships: {
    overline: "Brand Partnerships",
    headline: "Expand Your Business",
    description:
      `Connect with ${productStats.brandPartnerships.brands} Shopify brands. Sell their products in your store. Let them sell yours. No inventory risk, no upfront costs.`,
    benefits: [
      { icon: Globe, label: "Global Reach" },
      { icon: DollarSign, label: "Zero Inventory Cost" },
      { icon: HeartHandshake, label: "Partner Network" },
    ],
    audience: [
      {
        icon: Building2,
        title: "Suppliers",
        description: "Sell through partners",
      },
      {
        icon: ShoppingCart,
        title: "Retailers",
        description: "Expand without inventory",
      },
      {
        icon: Megaphone,
        title: "Ambassadors",
        description: "Earn on referrals",
      },
    ],
    cta: {
      primary: {
        text: "Install on Shopify",
        href: productStats.brandPartnerships.appStoreUrl,
      },
      secondary: {
        text: "Learn More",
        href: "/brand-partnerships",
      },
    },
    appStore: {
      rating: productStats.brandPartnerships.rating,
      reviews: productStats.brandPartnerships.reviews,
      href: productStats.brandPartnerships.appStoreUrl,
    },
  },
  aiAssistant: {
    overline: "Buddy AI Assistant",
    headline: "Stop Losing Sales",
    badge: "Built for Shopify",
    description:
      "Your 24/7 AI Sales, Support & Conversion team. Answers questions, recommends products, and closes deals — all trained on your store with zero setup.",
    benefits: [
      { icon: Rocket, label: "35% More Sales" },
      { icon: Clock, label: "24/7 Availability" },
      { icon: BarChart3, label: "Smart Analytics" },
    ],
    audience: [
      {
        icon: Store,
        title: "Any Store",
        description: "Works with all products",
      },
      {
        icon: UserCheck,
        title: "Conversion Focus",
        description: "Turn browsers to buyers",
      },
      {
        icon: Target,
        title: "Growth Stage",
        description: "Scale without staff",
      },
    ],
    cta: {
      primary: {
        text: "Try AI Assistant",
        href: productStats.aiAssistant.appStoreUrl,
      },
      secondary: {
        text: "Learn More",
        href: "/buddy-ai-assistant",
      },
    },
    appStore: {
      rating: productStats.aiAssistant.rating,
      reviews: productStats.aiAssistant.reviews,
      href: productStats.aiAssistant.appStoreUrl,
    },
  },
};

export function ProductsShowcaseSection() {
  return (
    <section className="py-16 md:py-20 bg-stone-50">
      <Container>
        <div className="space-y-12">
          {/* Brand Partnerships Showcase */}
          <motion.div
            id="brand-partnerships-showcase"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="scroll-mt-24"
          >
            <div className="relative rounded-3xl border-2 border-primary-200 bg-white/50 backdrop-blur-sm p-6 lg:p-10 overflow-hidden">
              {/* Thick border accent on right */}
              <div
                className="absolute top-0 bottom-0 right-0 hidden lg:block"
                style={{
                  width: "16px",
                  background:
                    "linear-gradient(180deg, rgba(16,185,129,0.4) 0%, rgba(16,185,129,0.9) 30%, rgba(16,185,129,1) 50%, rgba(16,185,129,0.9) 70%, rgba(16,185,129,0.4) 100%)",
                }}
              />

              <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
                {/* Content */}
                <div>
                  <p className="text-overline mb-4">
                    {showcaseData.brandPartnerships.overline}
                  </p>
                  <h3 className="text-display-1 text-stone-900 mb-4">
                    {showcaseData.brandPartnerships.headline}
                  </h3>
                  <p className="text-body-lg mb-8">
                    {showcaseData.brandPartnerships.description}
                  </p>

                  {/* Benefits */}
                  <div className="mb-8">
                    <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-3">
                      Benefits
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {showcaseData.brandPartnerships.benefits.map(
                        (benefit) => (
                          <span
                            key={benefit.label}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border bg-primary-50 border-primary-100"
                          >
                            <benefit.icon className="w-3.5 h-3.5 text-primary-600" />
                            <span className="text-xs font-medium text-primary-700">
                              {benefit.label}
                            </span>
                          </span>
                        )
                      )}
                    </div>
                  </div>

                  {/* Who it's for */}
                  <div className="mb-8">
                    <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-3">
                      Who it&apos;s for
                    </p>
                    <div className="grid sm:grid-cols-3 gap-3">
                      {showcaseData.brandPartnerships.audience.map((item) => (
                        <div
                          key={item.title}
                          className="p-3 rounded-xl border bg-primary-50/50 border-primary-100"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <item.icon className="w-4 h-4 text-primary-600" />
                            <span className="font-semibold text-stone-800 text-sm">
                              {item.title}
                            </span>
                          </div>
                          <p className="text-xs text-stone-500 leading-snug">
                            {item.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center justify-center gap-4">
                      <Button
                        href={showcaseData.brandPartnerships.cta.primary.href}
                        external
                        variant="primary"
                        size="lg"
                      >
                        {showcaseData.brandPartnerships.cta.primary.text}
                        <ArrowRight className="w-5 h-5" />
                      </Button>
                      <Button
                        href={showcaseData.brandPartnerships.cta.secondary.href}
                        variant="ghost"
                        size="lg"
                      >
                        {showcaseData.brandPartnerships.cta.secondary.text}
                        {/* <ArrowRight className="w-4 h-4" /> */}
                      </Button>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-4">
                      <a
                        href={showcaseData.brandPartnerships.appStore.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-stone-500 hover:text-stone-700 transition-colors"
                      >
                        <StarRating rating={showcaseData.brandPartnerships.appStore.rating} />
                        <span className="text-sm">
                          {showcaseData.brandPartnerships.appStore.rating} (
                          {showcaseData.brandPartnerships.appStore.reviews}{" "}
                          reviews)
                        </span>
                      </a>
                    </div>
                  </div>
                </div>

                {/* Illustration */}
                <div>
                  <Card
                    variant="gradient"
                    className="aspect-[4/3] flex items-center justify-center overflow-hidden"
                  >
                    <div className="w-full h-full">
                      <BrandPartnershipsIllustration />
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Buddy AI Assistant Showcase */}
          <motion.div
            id="ai-assistant-showcase"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="scroll-mt-24"
          >
            <div className="relative rounded-3xl border-2 border-stone-800 bg-stone-900 p-6 lg:p-10 overflow-hidden">
              {/* Thick border accent on left */}
              <div
                className="absolute top-0 bottom-0 left-0 hidden lg:block"
                style={{
                  width: "12px",
                  background:
                    "linear-gradient(180deg, rgba(34,197,94,0.4) 0%, rgba(34,197,94,0.9) 30%, rgba(34,197,94,1) 50%, rgba(34,197,94,0.9) 70%, rgba(34,197,94,0.4) 100%)",
                }}
              />

              <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
                {/* Illustration - on left for this one */}
                <div className="lg:order-1">
                  <Card
                    variant="gradient"
                    className="aspect-[4/3] flex items-center justify-center overflow-hidden"
                  >
                    <div className="w-full h-full">
                      <AiAssistantIllustration />
                    </div>
                  </Card>
                </div>

                {/* Content */}
                <div className="lg:order-2">
                  <div className="flex items-center gap-3 mb-4">
                    <p className="text-overline text-green-400">
                      {showcaseData.aiAssistant.overline}
                    </p>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-800 border border-stone-700">
                      <Gem className="w-3.5 h-3.5 text-green-400" />
                      <span className="text-xs font-semibold text-green-400">
                        {showcaseData.aiAssistant.badge}
                      </span>
                    </span>
                  </div>
                  <h3 className="text-display-1 text-white mb-4">
                    {showcaseData.aiAssistant.headline}
                  </h3>
                  <p className="text-body-lg text-stone-300 mb-8">
                    {showcaseData.aiAssistant.description}
                  </p>

                  {/* Benefits */}
                  <div className="mb-8">
                    <p className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-3">
                      Benefits
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {showcaseData.aiAssistant.benefits.map((benefit) => (
                        <span
                          key={benefit.label}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border bg-stone-800 border-stone-700"
                        >
                          <benefit.icon className="w-3.5 h-3.5 text-green-400" />
                          <span className="text-xs font-medium text-stone-200">
                            {benefit.label}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Who it's for */}
                  <div className="mb-8">
                    <p className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-3">
                      Who it&apos;s for
                    </p>
                    <div className="grid sm:grid-cols-3 gap-3">
                      {showcaseData.aiAssistant.audience.map((item) => (
                        <div
                          key={item.title}
                          className="p-3 rounded-xl border bg-stone-800/50 border-stone-700"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <item.icon className="w-4 h-4 text-green-400" />
                            <span className="font-semibold text-stone-200 text-sm">
                              {item.title}
                            </span>
                          </div>
                          <p className="text-xs text-stone-400 leading-snug">
                            {item.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center justify-center gap-4">
                      <Button
                        href={showcaseData.aiAssistant.cta.primary.href}
                        external
                        variant="primary"
                        size="lg"
                      >
                        {showcaseData.aiAssistant.cta.primary.text}
                        <ArrowRight className="w-5 h-5" />
                      </Button>
                      <Button
                        href={showcaseData.aiAssistant.cta.secondary.href}
                        variant="ghost"
                        size="lg"
                        className="text-stone-300 hover:text-white hover:bg-stone-800"
                      >
                        {showcaseData.aiAssistant.cta.secondary.text}
                        {/* <ArrowRight className="w-4 h-4" /> */}
                      </Button>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-4">
                      <a
                        href={showcaseData.aiAssistant.appStore.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-stone-400 hover:text-stone-200 transition-colors"
                      >
                        <StarRating rating={showcaseData.aiAssistant.appStore.rating} />
                        <span className="text-sm">
                          {showcaseData.aiAssistant.appStore.rating} (
                          {showcaseData.aiAssistant.appStore.reviews} reviews)
                        </span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </Container>
    </section>
  );
}
